import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import Product from '@/models/product';
import Order, { OrderStatus, PaymentOption, PaymentParameters } from '@/models/order';
import { UserInterface } from '@/models/user';
import Settings, { SettingNamespace } from '@/utils/settings';
import { District } from '@/utils/types';
import creditCardType, { types as CardType } from 'credit-card-type';
import { CreditCardType } from 'credit-card-type/dist/types';
import { OrderRetrievalMethod } from './time-scheduler';
import { pushGaEvent } from '@/utils/analytics';
export type OrderParameters = {
    user: UserInterface;
    scheduled_time: Date;
    is_picked_up: boolean;
};
export enum CheckoutStatus {
    FORM = 'form',
    LOADING = 'loading',
    ERROR = 'error',
    SUCCESS = 'success',
}
export type CheckoutError = {
    source: string;
    message: string;
};
export type CheckoutField = {
    label: string;
    value: string;
    required: boolean;
};
export const ALLOWED_CARDS = {
    [CardType.VISA]: 'VISA',
    [CardType.MASTERCARD]: 'MC',
};

function isNumeric(string: string) {
    return /^\d+$/.test(string);
}

function getCreditCardType(number: string) {
    const cardTypes = creditCardType(number).filter((card: CreditCardType) => Object.keys(ALLOWED_CARDS).indexOf(card.type) !== -1);
    if (cardTypes.length > 0) {
        return cardTypes[0];
    }
    return null;
}

function isValidCreditCard(number: string, type: any) {
    if (!isNumeric(number)) return false;
    if (type.lengths.length && number.length < type.lengths[0]) return false; // isMinimumLength for this card
    return true;
}

@Module({ name: 'cart', namespaced: true })
export default class CartModule extends VuexModule {
    products: Product[] = [];
    status: CheckoutStatus = CheckoutStatus.FORM;
    isOpen: boolean = false;
    lastOrder: Order | null = null;
    createAccount: boolean = false;
    registrationError: boolean = false;
    maxOrderProducts: number = 0;
    saveAddress: boolean = false;
    selectedDistrict: District = { name: '', fee: 0 };
    paymentOption: PaymentOption = PaymentOption.CARD;
    orderOptions: string[] = [];
    checkoutErrors: CheckoutError[] = [];
    checkoutFields: Record<string, CheckoutField> = {
        name: {
            label: 'Full name',
            value: '',
            required: true,
        },
        phone: {
            label: 'Phone number',
            value: '',
            required: true,
        },
        email: {
            label: 'Email',
            value: '',
            required: true,
        },
        membershipNumber: {
            label: 'Membership number',
            value: '',
            required: false,
        },
        deliveryAddress: {
            label: 'Delivery address',
            value: '',
            required: false,
        },
        businessName: {
            label: 'Business name',
            value: '',
            required: false,
        },
        deliveryInstructions: {
            label: 'Delivery instructions',
            value: '',
            required: false,
        },
        ccNumber: {
            label: 'Credit card number',
            value: '',
            required: true,
        },
        ccExpDate: {
            label: 'Credit card expiration date',
            value: '',
            required: true,
        },
        cvv: {
            label: 'CVV',
            value: '',
            required: true,
        },
    };
    showErrorAlert: boolean = false;
    tipAmount: number = 0;
    deliveryFee: number = 0;
    userAddressPlaceId: string = '';
    isMembershipApplied: boolean = false;
    extraFees: any[] = [];

    @Mutation
    public setExtraFees(extraFees: any[]) {
        this.extraFees = extraFees;
    }

    @Mutation
    public setIsOpen(isOpen: boolean) {
        this.isOpen = isOpen;
        if (!isOpen && this.status === CheckoutStatus.SUCCESS) {
            this.status = CheckoutStatus.FORM;
            this.lastOrder = null;
            this.registrationError = false;
        }
    }

    @Mutation
    public setLastOrder(order: Order) {
        this.lastOrder = order;
    }

    @Mutation
    public setCreateAccount(createAccount: boolean) {
        this.createAccount = createAccount;
    }

    @Mutation
    public setRegistrationError(error: boolean) {
        this.registrationError = error;
    }

    @Mutation
    public setFullName(name: string) {
        this.checkoutFields.name.value = name;
    }

    @Mutation
    public setEmail(email: string) {
        this.checkoutFields.email.value = email;
    }

    @Mutation
    public setMembershipNumber(membershipNumber: string) {
        this.checkoutFields.membershipNumber.value = membershipNumber;
    }

    @Mutation
    public setPhone(phone: string) {
        this.checkoutFields.phone.value = phone;
    }

    @Mutation
    public setDeliveryAddress(address: string) {
        this.checkoutFields.deliveryAddress.value = address;
    }

    @Mutation
    public setAddressPlaceId(placeId: string) {
        this.userAddressPlaceId = placeId;
    }

    @Mutation
    public setCCNumber(ccNumber: string) {
        this.checkoutFields.ccNumber.value = ccNumber;
    }

    @Mutation
    public setCVV(cvv: string) {
        this.checkoutFields.cvv.value = cvv;
    }

    @Mutation
    public setCCExpDate(ccExpDate: string) {
        this.checkoutFields.ccExpDate.value = ccExpDate;
    }

    @Mutation
    public setBusinessName(businessName: string) {
        this.checkoutFields.businessName.value = businessName;
    }

    @Mutation
    public setDeliveryInstructions(deliveryInstructions: string) {
        this.checkoutFields.deliveryInstructions.value = deliveryInstructions;
    }

    @Mutation
    public setSaveAddress(saveAddress: boolean) {
        this.saveAddress = saveAddress;
    }

    @Mutation
    public setPaymentOption(paymentOption: PaymentOption) {
        this.paymentOption = paymentOption;
    }

    @Mutation
    public setTipAmount(tip: number) {
        this.tipAmount = tip;
    }

    @Mutation
    public setDeliveryFee(fee: number) {
        this.deliveryFee = fee;
    }

    @Mutation
    public setShowErrorAlert(showAlert: boolean) {
        this.showErrorAlert = showAlert;
    }

    @Mutation
    public addProduct(product: Product) {
        const index = this.products.findIndex((prd: Product) => prd.id === product.id);
        if (index !== -1) {
            const updateProduct = new Product(this.products[index]);
            updateProduct.quantity! += product.quantity!;
            return this.products.splice(index, 1, updateProduct);
        }
        this.products.push(product);
    }

    @Mutation
    public clearProducts() {
        this.products = [];
    }

    @Mutation
    public removeProduct(product: Product) {
        const index = this.products.findIndex((prd: Product) => prd.id === product.id);
        if (index !== -1) {
            this.products.splice(index, 1);
        }
    }

    @Mutation
    public updateProduct(product: Product) {
        const index = this.products.findIndex((prd: Product) => prd.id === product.id);
        if (index !== -1) {
            this.products.splice(index, 1, product);
        }
    }

    @Mutation
    public addCheckoutError(error: CheckoutError) {
        const index = this.checkoutErrors.findIndex((err: CheckoutError) => err.source == error.source);
        if (index !== -1) {
            return this.checkoutErrors.splice(index, 1, error);
        }
        this.checkoutErrors.push(error);
    }

    @Mutation
    public clearCheckoutError(source: string) {
        const index = this.checkoutErrors.findIndex((err: CheckoutError) => err.source === source);
        if (index !== -1) {
            this.checkoutErrors.splice(index, 1);
        }
    }

    @Mutation
    public setCheckoutStatus(status: CheckoutStatus) {
        this.status = status;
    }

    @Mutation
    public setMaxOrderProducts(maxOrderProducts: number) {
        this.maxOrderProducts = maxOrderProducts;
    }

    @Mutation
    public clearAllCheckoutErrors() {
        this.checkoutErrors = [];
    }

    @Mutation
    public addOrderOption(optionId: string) {
        if (this.orderOptions.indexOf(optionId) === -1) {
            this.orderOptions.push(optionId);
        }
    }

    @Mutation
    public removeOrderOption(optionId: string) {
        const optionIndex = this.orderOptions.indexOf(optionId);
        if (optionIndex !== -1) {
            this.orderOptions.splice(optionIndex, 1);
        }
    }

    @Action({ commit: 'setMaxOrderProducts' })
    public async fetchMaxOrderProducts() {
        const settings = await Settings.getInstance();
        const maxOrderProducts = settings.get(SettingNamespace.ORDERS, 'max_order_products');
        return maxOrderProducts ? maxOrderProducts.value : 0;
    }

    @Action
    public async placeOrder(orderParameters: OrderParameters) {
        this.setCheckoutStatus(CheckoutStatus.LOADING);
        let order: Order | null = new Order({
            is_picked_up: orderParameters.is_picked_up,
            scheduled_time: orderParameters.scheduled_time,
            status: OrderStatus.PENDING,
            products: this.products,
            user: orderParameters.user,
            address: this.checkoutFields.deliveryAddress.value,
            business_name: this.checkoutFields.businessName.value,
            delivery_instructions: this.checkoutFields.deliveryInstructions.value,
            tip_amount: this.tipAmount,
            delivery_fee: !orderParameters.is_picked_up ? this.deliveryFee : 0,
            total: this.total,
            options: this.selectedOptions,
            payment_option: this.paymentOption,
        });

        if (!this.lastOrder) {
            order = await order!.save();
        } else if (this.lastOrder.id) {
            order.id = this.lastOrder.id;
            await order.update();
        }

        if (order) {
            const items = order.products.map((product: Product) => ({
                item_id: product.id!.toString(),
                item_name: product.is_preset ? product.name : 'Custom - ' + product.name,
                price: product.price,
                item_category: product.type.name,
                quantity: product.quantity!,
            }));

            pushGaEvent({
                event: 'begin_checkout',
                ecommerce: {
                    currency: 'KYD',
                    value: order.total,
                    items: items,
                },
            });

            const paymentParams = this.isMembershipApplied
                ? {
                      membership_applied: true,
                      token: this.checkoutFields.membershipNumber.value,
                  }
                : <PaymentParameters>{
                      cc_infos: {
                          number: this.checkoutFields.ccNumber.value,
                          exp_date: this.checkoutFields.ccExpDate.value,
                          cvv: this.checkoutFields.cvv.value,
                      },
                      amount: this.total,
                  };
            const paymentResponse = await order.payment(paymentParams);

            if (paymentResponse) {
                this.clearCheckoutError('payment');
                this.setLastOrder(order);

                if (paymentResponse.success) {
                    this.clearProducts();
                    this.clearCCFields();
                    this.setDeliveryAddress('');
                    this.setCheckoutStatus(CheckoutStatus.SUCCESS);
                    this.setSaveAddress(false);

                    pushGaEvent({
                        event: 'purchase',
                        leadsUserData: {
                            name: this.checkoutFields.name.value,
                            email: this.checkoutFields.email.value,
                            phone_number: this.checkoutFields.phone.value,
                        },
                        ecommerce: {
                            transaction_id: paymentResponse.content.transaction.id.toString(),
                            value: order.total,
                            currency: 'KYD',
                            items: items,
                            shipping: order.delivery_fee ?? 0,
                        },
                        tips: order.tip_amount ?? 0,
                        value_food: order.total - (order.delivery_fee ?? 0) - (order.tip_amount ?? 0),
                        order_type: order.is_picked_up ? 'pick_up' : 'delivery',
                    });

                    return order;
                }
                this.setCheckoutStatus(CheckoutStatus.FORM);
                this.addCheckoutError({
                    source: 'payment',
                    message: paymentResponse.content.message,
                });
                this.setShowErrorAlert(true);
                return false;
            }
            return false;
        }
        return null;
    }

    @Action
    public clearCCFields() {
        this.setCCNumber('');
        this.setCCExpDate('');
        this.setCVV('');
    }

    @Action
    public async validateCheckoutField(field: string) {
        this.clearCheckoutError('payment');
        this.setShowErrorAlert(false);
        this.checkoutFields['deliveryAddress'].required = this.isDelivery;
        this.checkoutFields['ccNumber'].required = !this.isMembershipApplied;
        this.checkoutFields['ccExpDate'].required = !this.isMembershipApplied;
        this.checkoutFields['cvv'].required = !this.isMembershipApplied;
        if (this.checkoutFields[field].required && (!this.checkoutFields[field].value || !this.checkoutFields[field].value.replace(/\s/g, '').length)) {
            this.addCheckoutError({
                source: field,
                message: this.checkoutFields[field].label + ' is required.',
            });
            return;
        }
        const creditCardRegex = /(?:^|[^0-9])(?:\d[ -]*?){13,16}(?:$|[^0-9])/g;
        switch (field) {
            case 'name': {
                if (/\d/.test(this.checkoutFields.name.value)) {
                    this.addCheckoutError({
                        source: 'name',
                        message: 'Please enter a valid name. No numbers allowed.',
                    });
                    break;
                }
            }
            case 'email': {
                const emailRegex = /\S+@\S+\.\S+/;
                if (!emailRegex.test(this.checkoutFields.email.value) || creditCardRegex.test(this.checkoutFields.email.value)) {
                    this.addCheckoutError({
                        source: 'email',
                        message: 'Please enter a valid email.',
                    });
                    break;
                }
            }
            case 'businessName': {
                if (creditCardRegex.test(this.checkoutFields.businessName.value)) {
                    this.checkoutFields.businessName.value = this.checkoutFields.businessName.value.replace(creditCardRegex, '');
                }
                break;
            }
            case 'deliveryInstructions': {
                if (creditCardRegex.test(this.checkoutFields.deliveryInstructions.value)) {
                    this.checkoutFields.deliveryInstructions.value = this.checkoutFields.deliveryInstructions.value.replace(creditCardRegex, '');
                }
                break;
            }
            case 'membershipNumber': {
                const isNumberValid = this.checkoutFields.membershipNumber.value.length === 13 && isNumeric(this.checkoutFields.membershipNumber.value);
                if (this.checkoutFields.membershipNumber.value.length > 0 && !isNumberValid) {
                    this.addCheckoutError({
                        source: 'membershipNumber',
                        message: 'The membership number must contains 13 digits.',
                    });
                }
                break;
            }
            case 'ccExpDate': {
                if (this.isMembershipApplied) {
                    break;
                }
                const year = new Date().getFullYear().toString().substring(2);
                const month = new Date().getMonth() + 1;
                if (this.checkoutFields.ccExpDate.value.length < 5 && this.checkoutFields.ccExpDate.value.length > 0) {
                    this.addCheckoutError({
                        source: 'ccExpDate',
                        message: 'Expiration date not valid.',
                    });
                    break;
                }
                if ((this.checkoutFields.ccExpDate.value.length === 5 && this.checkoutFields.ccExpDate.value.slice(3, 5) < year) || (this.checkoutFields.ccExpDate.value.slice(3, 5) == year && parseInt(this.checkoutFields.ccExpDate.value.slice(0, 2)) < month)) {
                    this.addCheckoutError({
                        source: 'ccExpDate',
                        message: 'Credit card is expired.',
                    });
                    break;
                }
            }
            case 'ccNumber': {
                if (this.isMembershipApplied) {
                    break;
                }
                if (this.checkoutFields.ccNumber.value.length < 1) {
                    break;
                }
                if (!this.ccType) {
                    this.addCheckoutError({
                        source: 'ccNumber',
                        message: 'This type of credit card is not supported.',
                    });
                    break;
                }
                const creditCardType = getCreditCardType(this.checkoutFields.ccNumber.value);
                if (!creditCardType || !isValidCreditCard(this.checkoutFields.ccNumber.value, creditCardType)) {
                    this.addCheckoutError({
                        source: 'ccNumber',
                        message: 'This credit card format is invalid.',
                    });
                    break;
                }
            }
            default:
                break;
        }
    }

    @Mutation
    public applyMembership(success: boolean) {
        this.isMembershipApplied = success;
    }

    @Mutation
    @Action
    public validatePhone(phoneObject: any) {
        if (phoneObject.formatted !== '' && !phoneObject.valid) {
            return this.addCheckoutError({
                source: 'phone',
                message: 'Please enter a valid phone number.',
            });
        } else if (phoneObject.formatted === '') {
            return this.addCheckoutError({
                source: 'phone',
                message: 'Phone number is required',
            });
        }
        return this.clearCheckoutError('phone');
    }

    get selectedOptions(): Record<string, boolean> {
        return this.orderOptions.reduce((map, orderOption) => {
            map[orderOption] = true;
            return map;
        }, <Record<string, boolean>>{});
    }

    get deliveryDistrict(): string {
        return this.selectedDistrict.name;
    }

    get total(): number {
        return this.subtotal - (this.isMembershipApplied ? this.subtotal * 0.05 : 0) + (this.isDelivery ? this.deliveryFee : 0) + this.tipAmount + this.extraFees.reduce((accumulator: number, fee: any) => accumulator + (this.subtotal * fee.amount) / 100, 0);
    }

    get isDelivery(): boolean {
        return this.context.rootState.timescheduler.retrievalMethod === OrderRetrievalMethod.DELIVERY;
    }

    get subtotal(): number {
        return this.products.reduce((accumulator: number, product: Product) => {
            const productPrice = product.quantity ? product.quantity * product.price : 0;
            return accumulator + productPrice;
        }, 0);
    }

    get ccType(): string | null {
        if (this.checkoutFields.ccNumber.value.length) {
            const ccType = getCreditCardType(this.checkoutFields.ccNumber.value);
            if (ccType && ccType.type) {
                const creditCardType = ALLOWED_CARDS[ccType.type];
                if (creditCardType) {
                    return creditCardType;
                }
            }
        }
        return null;
    }

    get productsQuantity(): number {
        return <number>this.products
            .map((product) => product.quantity)
            .reduce((previousQty: number | undefined, currentQty: number | undefined) => {
                return previousQty! + (currentQty !== undefined ? currentQty : 1);
            }, 0);
    }

    get maxProductsReached() {
        if (this.productsQuantity >= this.maxOrderProducts) {
            return true;
        }
        return false;
    }

    get hasError() {
        return (error: string) => this.checkoutErrors.findIndex((err: CheckoutError) => err.source === error) !== -1;
    }
}
